import { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout, NotificationPopup, DailyEnergyPopup, hasClaimedDailyEnergy, BalanceAdjustmentPopup, WithdrawalCompletedPopup } from './components';
import { HomePage, TasksPage, EarnPage, OffersPage, GamesPage, ReferralPage, ProfilePage, RedeemPage, SupportPage, AdvertiserPage } from './pages';
import { useAppStore } from './store/useAppStore';
import { useConfigStore } from './store/useConfigStore';
import { useTelegram, initRichAds, useSocket } from './hooks';
import { authApi } from './api';

// Telegram channel link
const TELEGRAM_CHANNEL_LINK = 'https://t.me/+U9yeazpMLSliOWI9';

// Key for storing pending redeem code
const PENDING_REDEEM_CODE_KEY = 'gemifaucet_pending_redeem_code';

// Key for tracking if balance migration was done
const BALANCE_MIGRATED_KEY = 'gemifaucet_balance_migrated';

// Helper to get old cached balance from localStorage (for migration)
function getOldCachedBalance(): number | null {
  try {
    const stored = localStorage.getItem('gemifaucet-storage');
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    const balance = parsed?.state?.user?.balance;
    
    // Return balance only if it's a valid positive number
    if (typeof balance === 'number' && balance > 0 && Number.isFinite(balance)) {
      return balance;
    }
    return null;
  } catch {
    return null;
  }
}

function App() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showDailyEnergyPopup, setShowDailyEnergyPopup] = useState(false);

  const [checkingChannel, setCheckingChannel] = useState(false);
  const [channelError, setChannelError] = useState<string | null>(null);
  const [balanceAdjustment, setBalanceAdjustment] = useState<{
    type: 'credit' | 'debit';
    amount: number;
    newBalance: number;
    reason: string | null;
  } | null>(null);
  const [withdrawalCompleted, setWithdrawalCompleted] = useState<{
    withdrawalId: string;
    amount: number;
    paymentMethod: string;
    transactionRef: string | null;
  } | null>(null);
  
  const { webApp, isAvailable, openTelegramLink, hapticFeedback, hapticNotification } = useTelegram();
  const setUser = useAppStore((state) => state.setUser);
  const setToken = useAppStore((state) => state.setToken);
  const user = useAppStore((state) => state.user);
  const fetchConfig = useConfigStore((state) => state.fetchConfig);
  const maintenanceMode = useConfigStore((state) => state.maintenanceMode);
  const maintenanceMessage = useConfigStore((state) => state.maintenanceMessage);

  // Socket connection for real-time notifications - only connect after user is authenticated
  const handleBalanceAdjustment = useCallback((data: {
    type: 'credit' | 'debit';
    amount: number;
    newBalance: number;
    reason: string | null;
  }) => {
    setBalanceAdjustment({
      type: data.type,
      amount: data.amount,
      newBalance: data.newBalance,
      reason: data.reason,
    });
  }, []);

  const handleWithdrawalCompleted = useCallback((data: {
    withdrawalId: string;
    amount: number;
    paymentMethod: string;
    transactionRef: string | null;
  }) => {
    setWithdrawalCompleted({
      withdrawalId: data.withdrawalId,
      amount: data.amount,
      paymentMethod: data.paymentMethod,
      transactionRef: data.transactionRef,
    });
  }, []);

  // Only initialize socket after user is loaded
  useSocket(user ? { 
    onBalanceAdjustment: handleBalanceAdjustment,
    onWithdrawalCompleted: handleWithdrawalCompleted,
  } : {});

  // Check if user has seen welcome screen
  const hasSeenWelcome = useCallback(() => {
    return localStorage.getItem('gemifaucet_welcomed') === 'true';
  }, []);

  const markWelcomeSeen = useCallback(() => {
    localStorage.setItem('gemifaucet_welcomed', 'true');
  }, []);

  // Initialize Onclicka inpage ads - loads only once
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js.onclckmn.com/static/onclicka.js';
    script.dataset.admpid = '403427';
    script.async = true;
    document.head.appendChild(script);
    
    return () => {
      // Cleanup on unmount (optional, but good practice)
      const existingScript = document.querySelector('script[data-admpid="403427"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // Handle join channel click
  const handleJoinChannel = useCallback(() => {
    hapticFeedback('medium');
    openTelegramLink(TELEGRAM_CHANNEL_LINK);
  }, [hapticFeedback, openTelegramLink]);

  // Handle skip channel joining
  const handleSkipChannel = useCallback(() => {
    hapticFeedback('light');
    markWelcomeSeen();
    setShowWelcome(false);
  }, [hapticFeedback, markWelcomeSeen]);

  // Handle continue after joining
  const handleContinue = useCallback(async () => {
    setCheckingChannel(true);
    setChannelError(null);
    hapticFeedback('light');
    
    try {
      // Verify channel membership via backend
      const result = await authApi.checkChannelMembership();
      
      if (result.isMember) {
        hapticNotification('success');
        markWelcomeSeen();
        setShowWelcome(false);
      } else {
        hapticNotification('error');
        setChannelError('You haven\'t joined our channel yet. Please join to continue.');
      }
    } catch (err: any) {
      console.error('Channel check error:', err);
      hapticNotification('error');
      setChannelError('Failed to verify. Please try again.');
    } finally {
      setCheckingChannel(false);
    }
  }, [hapticFeedback, hapticNotification, markWelcomeSeen]);

  useEffect(() => {
    const init = async () => {
      try {
        // Fetch platform config first - always force refresh to check maintenance mode
        await fetchConfig(true);
        
        // Check if we have a stored token but no user (restored from localStorage)
        const storedToken = localStorage.getItem('auth_token') || user ? localStorage.getItem('gemifaucet-storage') : null;
        if (storedToken && !user) {
          console.log('Restoring user profile from stored token...');
          try {
            const profile = await authApi.getProfile();
            if (profile?.telegramId) {
              setUser({
                telegramId: profile.telegramId,
                username: profile.username,
                firstName: profile.firstName,
                balance: profile.balance,
                totalEarned: profile.totalEarned || profile.balance,
                level: profile.level,
                experience: profile.experience,
                streak: profile.streak?.count || 0,
                referralCode: profile.referral?.code || '',
                kycVerified: profile.kycStatus || false,
                isBanned: profile.isBanned,
                banReason: profile.banReason,
              });
              console.log('User profile restored successfully');
              setLoading(false);
              return;
            }
          } catch (err) {
            console.warn('Failed to restore user profile, will re-authenticate:', err);
          }
        }
        
        // Get init data from Telegram WebApp
        const initData = webApp?.initData;
        const startParam = webApp?.initDataUnsafe?.start_param || '';
        
        // Check if start_param is a redeem code (format: redeem_XXXX) or ticket (format: ticket_XXXX)
        let referralCode = new URLSearchParams(window.location.search).get('ref') || '';
        
        if (startParam.startsWith('redeem_')) {
          // Extract redeem code and store it for later use
          const redeemCode = startParam.replace('redeem_', '');
          // Format the code with hyphens (every 4 characters)
          const formattedCode = redeemCode.match(/.{1,4}/g)?.join('-') || redeemCode;
          sessionStorage.setItem(PENDING_REDEEM_CODE_KEY, formattedCode);
          console.log('Redeem code detected from deep link:', formattedCode);
        } else if (startParam.startsWith('ticket_')) {
          // Extract ticket ID and store it for navigation after auth
          // Format: ticket_TKT2512XXXXXX -> TKT-2512-XXXXXX
          const ticketIdRaw = startParam.replace('ticket_', '');
          // Format the ticket ID: TKT2512XXXXXX -> TKT-2512-XXXXXX
          const formattedTicketId = ticketIdRaw.replace(/^(TKT)(\d{4})(.+)$/, '$1-$2-$3');
          sessionStorage.setItem('gemifaucet_pending_ticket', formattedTicketId);
          console.log('Ticket deep link detected:', formattedTicketId);
        } else if (startParam) {
          // It's a referral code
          referralCode = startParam;
        }

        if (!initData || !isAvailable) {
          // Development mode - create mock user
          if (import.meta.env.DEV) {
            console.log('Running in development mode with mock user');
            const mockUser = {
              telegramId: '123456789',
              username: 'testuser',
              firstName: 'Test',
              lastName: 'User',
              balance: 1000,
              totalEarned: 5000,
              level: 5,
              experience: 250,
              streak: 3,
              referralCode: 'TEST123',
              kycVerified: false,
            };
            setUser(mockUser);
            setToken('dev-token');
            setLoading(false);
            return;
          }
          throw new Error('Telegram WebApp not available. Please open this app from Telegram.');
        }

        console.log('Authenticating with backend...');
        
        // Retry logic with exponential backoff
        let retries = 0;
        const maxRetries = 3;
        let lastError: any = null;

        while (retries < maxRetries) {
          try {
            // Authenticate with backend
            const response = await authApi.authenticate(initData, referralCode || undefined);
            
            if (response.token && response.user) {
              setUser({
                telegramId: response.user.telegramId,
                username: response.user.username,
                firstName: response.user.firstName,
                balance: response.user.balance,
                totalEarned: response.user.balance,
                level: response.user.level,
                experience: response.user.experience,
                streak: response.user.streak,
                referralCode: response.user.referralCode,
                kycVerified: false,
                isBanned: response.user.isBanned,
                banReason: response.user.banReason,
              });
              setToken(response.token);
              
              // Store token for API calls
              localStorage.setItem('auth_token', response.token);
              console.log('Authentication successful');
              
              // Initialize RichAds Telegram SDK
              if (initRichAds()) {
                console.log('RichAds initialized');
              }

              // Check if there's a pending ticket deep link - skip welcome screen for existing users
              const hasPendingTicket = sessionStorage.getItem('gemifaucet_pending_ticket');
              
              // Check if new user or hasn't seen welcome
              // BUT skip welcome if coming from ticket deep link (user definitely has an account)
              if ((response.user.isNewUser || !hasSeenWelcome()) && !hasPendingTicket) {
                setShowWelcome(true);
              } else if (hasPendingTicket) {
                // User came from ticket deep link, mark as welcomed so they don't see it next time
                console.log('Ticket deep link detected, skipping welcome screen');
                markWelcomeSeen();
              }

              return; // Success, exit retry loop
            } else {
              throw new Error('Authentication failed - Invalid response');
            }
          } catch (err: any) {
            lastError = err;
            
            // Check if it's a rate limit error
            if (err.response?.status === 429) {
              const retryAfter = err.response?.data?.retryAfter || 60;
              console.warn(`Rate limited. Waiting ${retryAfter}s before retry...`);
              
              // Wait before retrying
              await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
              retries++;
              continue;
            }
            
            // For other errors, retry with exponential backoff
            if (retries < maxRetries - 1) {
              const delay = Math.pow(2, retries) * 1000; // 1s, 2s, 4s
              console.warn(`Auth failed, retrying in ${delay/1000}s... (${retries + 1}/${maxRetries})`);
              await new Promise(resolve => setTimeout(resolve, delay));
              retries++;
            } else {
              throw err; // Max retries reached
            }
          }
        }
        
        // If we get here, all retries failed
        throw lastError || new Error('Authentication failed after multiple attempts');
        
      } catch (err: any) {
        console.error('Init error:', err);
        
        // Provide user-friendly error messages
        let errorMessage = 'Failed to initialize app';
        
        if (err.response?.status === 429) {
          errorMessage = 'Too many requests. Please wait a moment and try again.';
        } else if (err.response?.status === 503) {
          errorMessage = 'Service temporarily unavailable. Please try again later.';
        } else if (err.message?.includes('Network')) {
          errorMessage = 'Network error. Please check your connection.';
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    // Small delay to ensure Telegram WebApp is fully loaded
    const timer = setTimeout(init, 100);
    return () => clearTimeout(timer);
  }, [webApp, isAvailable, setUser, setToken, fetchConfig, hasSeenWelcome]);

  // Navigate to redeem page if there's a pending code
  useEffect(() => {
    if (!loading && user) {
      const pendingCode = sessionStorage.getItem(PENDING_REDEEM_CODE_KEY);
      if (pendingCode && window.location.pathname !== '/redeem') {
        // Navigate to redeem page
        window.location.href = '/redeem';
      }
    }
  }, [loading, user]);

  // Periodic balance sync to prevent stale balance issues
  // Also handles one-time migration of old cached balance to server
  useEffect(() => {
    if (!user || loading) return;

    const syncBalance = async () => {
      try {
        // One-time migration: Check if there's an old cached balance to sync to server
        const alreadyMigrated = localStorage.getItem(BALANCE_MIGRATED_KEY) === 'true';
        if (!alreadyMigrated) {
          const oldCachedBalance = getOldCachedBalance();
          if (oldCachedBalance && oldCachedBalance > 0) {
            console.log('Found old cached balance to migrate:', oldCachedBalance);
            try {
              const syncResult = await authApi.syncCachedBalance(oldCachedBalance);
              if (syncResult.success) {
                console.log('Balance migration result:', syncResult);
                localStorage.setItem(BALANCE_MIGRATED_KEY, 'true');
                
                // If balance was synced, use the new balance
                if (syncResult.synced && syncResult.newBalance !== undefined) {
                  setUser({
                    ...useAppStore.getState().user!,
                    balance: syncResult.newBalance,
                  });
                  return; // Skip the regular sync since we just updated
                }
              }
            } catch (err) {
              console.warn('Balance migration failed:', err);
            }
          } else {
            // No old balance to migrate, mark as done
            localStorage.setItem(BALANCE_MIGRATED_KEY, 'true');
          }
        }

        // Regular balance sync from server
        const profile = await authApi.getProfile();
        if (profile?.balance !== undefined) {
          // Only update if balance changed to avoid unnecessary re-renders
          const currentBalance = useAppStore.getState().user?.balance;
          const currentBanned = useAppStore.getState().user?.isBanned;
          if (currentBalance !== profile.balance || currentBanned !== profile.isBanned) {
            console.log('Balance synced from server:', profile.balance);
            setUser({
              ...useAppStore.getState().user!,
              balance: profile.balance,
              totalEarned: profile.totalEarned || profile.balance,
              isBanned: profile.isBanned,
              banReason: profile.banReason,
            });
          }
        }
      } catch (err) {
        // Silent fail - don't disrupt user experience
        console.warn('Balance sync failed:', err);
      }
    };

    // Sync immediately on mount
    syncBalance();

    // Then sync every 30 seconds
    const interval = setInterval(syncBalance, 30000);
    return () => clearInterval(interval);
  }, [user?.telegramId, loading, setUser]);

  // Check for daily free energy popup
  useEffect(() => {
    // Show daily energy popup when:
    // - User is loaded and authenticated
    // - Welcome screen is not showing
    // - User hasn't claimed daily energy today
    if (!loading && user && !showWelcome && !hasClaimedDailyEnergy()) {
      // Small delay to let the UI settle
      const timer = setTimeout(() => {
        setShowDailyEnergyPopup(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, user, showWelcome]);

  // Check for bot start status and show popup if needed
  useEffect(() => {
    const checkBotStatus = async () => {
      // Bot status check removed - no longer showing start bot popup
    };

    checkBotStatus();
  }, [loading, user, showWelcome, showDailyEnergyPopup]);

  // Loading screen
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 relative">
            <div className="absolute inset-0 border-4 border-blue-500/20 rounded-full" />
            <div className="absolute inset-0 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
          <h1 className="text-xl font-bold mb-2 text-white">Gemifaucet</h1>
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  // Error screen
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
            <span className="text-3xl">⚠️</span>
          </div>
          <h1 className="text-xl font-bold mb-2 text-white">Something went wrong</h1>
          <p className="text-white/60 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-xl bg-blue-500 text-white font-medium hover:bg-blue-600 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Maintenance mode screen
  if (maintenanceMode) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto mb-4 bg-yellow-500/20 rounded-full flex items-center justify-center">
            <span className="text-3xl">🔧</span>
          </div>
          <h1 className="text-xl font-bold mb-2 text-white">Under Maintenance</h1>
          <p className="text-white/60 mb-4">
            {maintenanceMessage || 'We are currently performing scheduled maintenance. Please check back soon.'}
          </p>
          <button
            onClick={async () => {
              await fetchConfig(true);
              // Reload if maintenance is disabled
              if (!useConfigStore.getState().maintenanceMode) {
                window.location.reload();
              }
            }}
            className="px-6 py-3 rounded-xl bg-yellow-500 text-black font-medium hover:bg-yellow-600 transition"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  // Welcome / Channel Join screen
  if (showWelcome) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-sm w-full"
        >
          {/* Logo / Icon */}
          <motion.div 
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            transition={{ delay: 0.1 }}
            className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-2xl"
          >
            <span className="text-5xl">💎</span>
          </motion.div>

          {/* Welcome text */}
          <motion.h1 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-2xl font-bold mb-2 text-white"
          >
            Welcome to Gemifaucet!
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-white/60 mb-8"
          >
            {user?.firstName ? `Hey ${user.firstName}! ` : ''}
            Join our Telegram channel to get started and never miss updates, giveaways & announcements!
          </motion.p>

          {/* Join Channel Button */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            onClick={handleJoinChannel}
            className="w-full px-6 py-4 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 mb-4"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
            </svg>
            Join Our Channel
          </motion.button>

          {/* Continue Button */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            onClick={handleContinue}
            disabled={checkingChannel}
            className="w-full px-6 py-4 rounded-2xl bg-white/10 hover:bg-white/20 text-white font-medium text-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {checkingChannel ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                I've Joined, Continue
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </>
            )}
          </motion.button>

          {/* Skip Button */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            onClick={handleSkipChannel}
            className="w-full mt-3 px-4 py-2 text-white/50 hover:text-white/70 text-sm font-medium transition-all flex items-center justify-center gap-1"
          >
            Skip for now
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </motion.button>

          {/* Error message */}
          {channelError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-3 rounded-xl bg-red-500/20 border border-red-500/30"
            >
              <p className="text-red-300 text-sm flex items-center gap-2">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                {channelError}
              </p>
            </motion.div>
          )}

          {/* Info text */}
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-white/40 text-sm mt-6"
          >
            Join to get exclusive updates & promo codes!
          </motion.p>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      {/* Notification Popup for refunds and other alerts */}
      <NotificationPopup />
      
      {/* Balance Adjustment Popup - real-time from admin */}
      <BalanceAdjustmentPopup 
        data={balanceAdjustment}
        onClose={() => setBalanceAdjustment(null)}
      />
      
      {/* Withdrawal Completed Popup - real-time from admin */}
      <WithdrawalCompletedPopup 
        data={withdrawalCompleted}
        onClose={() => setWithdrawalCompleted(null)}
      />
      
      {/* Daily Free Energy Popup */}
      <DailyEnergyPopup 
        isOpen={showDailyEnergyPopup} 
        onClose={() => setShowDailyEnergyPopup(false)}
        onSuccess={(newEnergy) => {
          console.log('Daily energy claimed! New energy:', newEnergy);
          setShowDailyEnergyPopup(false);
        }}
      />
      

      
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<TasksPage />} />
          <Route path="home" element={<HomePage />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="earn" element={<EarnPage />} />
          <Route path="offers" element={<OffersPage />} />
          <Route path="games" element={<GamesPage />} />
          <Route path="redeem" element={<RedeemPage />} />
          <Route path="referral" element={<ReferralPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="advertiser" element={<AdvertiserPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </>
  );
}

export default App;